import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import {
  faArrowLeft,
  faPencil,
  faTrash,
  faPlus,
  faCheck,
  faTimes,
  faTicket,
  faBookmark,
  faCalendarAlt,
  faLoveseat,
  faChevronDown,
  faFileInvoice,
} from '@fortawesome/pro-light-svg-icons';
import { faPercent, faTag } from '@fortawesome/pro-regular-svg-icons';
import { ReservasService } from 'src/app/services/reservas.service';
import { DialogGenericComponent } from 'src/app/components/dialog-generic/dialog-generic.component';
import { DialogReservationPayComponent } from 'src/app/components/dialog-reservation-pay/dialog-reservation-pay.component';
import { AppSettings } from 'src/app/app-settings';
import { sortGroupsByNearestSession } from 'src/app/helpers/reservation-groups';

/**
 * ReservaDetailComponent - Detalle de una reserva: líneas, edición y compra.
 *
 * Flujo:
 * - Muestra las líneas (entradas/asientos) de la reserva.
 * - Permite editar cantidad, eliminar líneas, renombrar o eliminar la reserva.
 * - "Añadir entradas" navega a /home con reservationId como query param,
 *   que se propaga a event-sale para activar el modo reserva en el mapa.
 * - "Finalizar compra" abre el diálogo de pago de reserva.
 */
@Component({
  selector: 'app-reserva-detail',
  templateUrl: './reserva-detail.component.html',
  styleUrls: ['./reserva-detail.component.scss'],
})
export class ReservaDetailComponent {
  faArrowLeft = faArrowLeft;
  faPencil = faPencil;
  faTrash = faTrash;
  faPlus = faPlus;
  faCheck = faCheck;
  faTimes = faTimes;
  faTicket = faTicket;
  faBookmark = faBookmark;
  faCalendar = faCalendarAlt;
  faLoveseat = faLoveseat;
  faChevron = faChevronDown;
  faFileInvoice = faFileInvoice;
  faPercent = faPercent;
  faTag = faTag;
  urlImages = AppSettings.API_EVENTS_ASSETS;

  private _groupState = new Map<string, boolean>();

  reservationId!: number;
  reservation: any = null;
  loading: boolean = true;
  purchaseLoading: boolean = false;

  // Edición inline del nombre
  editingName: boolean = false;
  editNameValue: string = '';
  discountExpanded: boolean = false;

  // Edición inline de cantidad de línea
  editingLineId: number | null = null;
  editLineQuantity: number = 1;

  constructor(
    private activatedRoute: ActivatedRoute,
    private reservasService: ReservasService,
    private router: Router,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.activatedRoute.params.subscribe((params) => {
      this.reservationId = Number(params['id']);
      this.loadReservation();
    });
  }

  loadReservation(): void {
    this.loading = true;
    this.reservasService.getReservation(this.reservationId).subscribe({
      next: (res) => {
        this.reservation = res.data || res;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.showError('No se ha podido cargar la reserva.');
      },
    });
  }

  get reservationLines(): any[] {
    return this.reservation?.cart?.cart_lines || [];
  }

  get reservationTotal(): number {
    return this.reservationLines.reduce(
      (acc, line) => acc + (line.price_with_discount ?? (+line.price - +line.fee)),
      0
    );
  }

  get totalQuantity(): number {
    return this.reservationLines.reduce((acc, line) => acc + (line.quantity || 1), 0);
  }

  get groupedLines(): { event: any; session: any; groupKey: string; lines: any[]; total: number; quantityTotal: number; expanded: boolean }[] {
    const map = new Map<string, { event: any; session: any; groupKey: string; lines: any[]; total: number; quantityTotal: number; expanded: boolean }>();
    for (const line of this.reservationLines) {
      const key = `${line.event?.id}_${line.session?.id}`;
      if (!map.has(key)) {
        const existing = this._groupState.get(key);
        map.set(key, { event: line.event, session: line.session, groupKey: key, lines: [], total: 0, quantityTotal: 0, expanded: existing !== undefined ? existing : true });
      }
      const group = map.get(key)!;
      group.lines.push(line);
      group.quantityTotal += (line.quantity || 1);
      group.total += (line.price_with_discount ?? (+line.price - +line.fee)) * (line.quantity || 1);
    }
    return sortGroupsByNearestSession(Array.from(map.values()));
  }

  toggleGroup(groupKey: string): void {
    const current = this._groupState.has(groupKey) ? this._groupState.get(groupKey)! : true;
    this._groupState.set(groupKey, !current);
  }

  trackByGroup(_index: number, group: any): string {
    return group.groupKey;
  }

  trackByLine(_index: number, line: any): number {
    return line.id;
  }

  // --- Navegación ---

  /** Navega al listado de eventos en modo reserva */
  addTickets(): void {
    this.reservasService.setActiveReservation(this.reservationId);
    this.router.navigate(['/home'], {
      queryParams: { reservationId: this.reservationId },
    });
  }

  goBack(): void {
    this.router.navigate(['/reservas']);
  }

  // --- Edición del nombre ---

  startEditName(): void {
    this.editNameValue = this.reservation.name;
    this.editingName = true;
  }

  cancelEditName(): void {
    this.editingName = false;
    this.editNameValue = '';
  }

  saveName(): void {
    if (!this.editNameValue?.trim()) return;
    this.reservasService
      .updateReservation(this.reservationId, this.editNameValue.trim())
      .subscribe({
        next: (res) => {
          this.reservation.name = this.editNameValue.trim();
          this.editingName = false;
        },
        error: () => this.showError('No se ha podido actualizar el nombre.'),
      });
  }

  // --- Líneas ---

  startEditLine(line: any): void {
    this.editingLineId = line.id;
    this.editLineQuantity = line.quantity || 1;
  }

  cancelEditLine(): void {
    this.editingLineId = null;
  }

  saveLineQuantity(line: any): void {
    if (this.editLineQuantity < 1) return;
    this.reservasService
      .updateLine(this.reservationId, line.id, this.editLineQuantity)
      .subscribe({
        next: () => {
          line.quantity = this.editLineQuantity;
          this.editingLineId = null;
          this.loadReservation(); // Refrescar totales
        },
        error: () => this.showError('No se ha podido actualizar la línea.'),
      });
  }

  deleteLine(line: any): void {
    this.dialog.open(DialogGenericComponent, {
      data: {
        title: 'Eliminar entrada',
        subtitle: `¿Seguro que quieres eliminar esta entrada de la reserva? El asiento quedará libre.`,
        icon: 'warning',
        buttons: [
          {
            label: 'Cancelar',
            type: 'secondary',
            action: () => this.dialog.closeAll(),
          },
          {
            label: 'Eliminar',
            type: 'danger',
            action: () => {
              this.dialog.closeAll();
              this.confirmDeleteLine(line.id);
            },
          },
        ],
      },
    });
  }

  private confirmDeleteLine(lineId: number): void {
    this.reservasService.deleteLine(this.reservationId, lineId).subscribe({
      next: () => this.loadReservation(),
      error: () => this.showError('No se ha podido eliminar la línea.'),
    });
  }

  // --- Eliminar reserva ---

  deleteReservation(): void {
    this.dialog.open(DialogGenericComponent, {
      data: {
        title: 'Eliminar reserva',
        subtitle: `¿Seguro que quieres eliminar la reserva "${this.reservation.name}"? Esta acción liberará todos los asientos y no se puede deshacer.`,
        icon: 'warning',
        buttons: [
          {
            label: 'Cancelar',
            type: 'secondary',
            action: () => this.dialog.closeAll(),
          },
          {
            label: 'Eliminar reserva',
            type: 'danger',
            action: () => {
              this.dialog.closeAll();
              this.confirmDeleteReservation();
            },
          },
        ],
      },
    });
  }

  private confirmDeleteReservation(): void {
    this.reservasService.deleteReservation(this.reservationId).subscribe({
      next: () => this.router.navigate(['/reservas']),
      error: () => this.showError('No se ha podido eliminar la reserva.'),
    });
  }

  // --- Finalizar compra ---

  finalizePurchase(): void {
    const dialogRef = this.dialog.open(DialogReservationPayComponent, {
      width: '500px',
      data: {
        reservationId: this.reservationId,
        total: this.reservationTotal,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.success) {
        if (result.invoicingId) {
          this.router.navigate(['/refunds', result.invoicingId]);
        } else {
          this.router.navigate(['/reservas']);
        }
      }
    });
  }

  // --- Helpers ---

  get isReserved(): boolean {
    return this.reservation?.cart?.status?.name === 'reserved';
  }

  get totalOrder(): any {
    return this.reservation?.cart?.total_order || null;
  }

  get appliedDiscounts(): any[] {
    const d = this.reservation?.cart?.applied_discounts;
    const arr = Array.isArray(d) ? d : (typeof d === 'string' ? (() => { try { return JSON.parse(d); } catch { return []; } })() : []);
    return arr.filter((disc: any) => disc.type !== 'promocode');
  }

  get promoCodeDiscounts(): any[] {
    const d = this.reservation?.cart?.applied_discounts;
    const arr = Array.isArray(d) ? d : (typeof d === 'string' ? (() => { try { return JSON.parse(d); } catch { return []; } })() : []);
    return arr.filter((disc: any) => disc.type === 'promocode');
  }

  get hasDiscounts(): boolean {
    return (this.totalOrder?.discount_total ?? 0) > 0;
  }

  get isReadOnly(): boolean {
    const s = this.reservation?.cart?.status?.name;
    return s === 'completed' || s === 'paid' || s === 'cancelled' || s === 'deleted';
  }

  get orderInvoicingId(): number | null {
    const orders = this.reservation?.cart?.orders;
    if (Array.isArray(orders) && orders.length > 0) {
      return orders[0].id_invoicing || null;
    }
    return null;
  }

  goToOrder(): void {
    if (this.orderInvoicingId) {
      this.router.navigate(['/refunds', this.orderInvoicingId]);
    }
  }

  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      reserved: 'Reservada',
      pending: 'Pendiente',
      active: 'Activa',
      processing: 'Procesando pago',
      paid: 'Pagada',
      completed: 'Completada',
      cancelled: 'Cancelada',
      deleted: 'Eliminada',
    };
    return labels[status] ?? status ?? 'Pendiente';
  }

  getLineSeatInfo(line: any): string {
    if (line.bookings?.length > 0) {
      const b = line.bookings[0];
      const seat = b.seat;
      if (seat) {
        const parts: string[] = [];
        if (seat.map_section?.name) parts.push(seat.map_section.name);
        if (seat.row) parts.push(`Fila ${seat.row}`);
        if (seat.seat) parts.push(`Butaca ${seat.seat}`);
        return parts.join(' · ');
      }
      if (b.row || b.section_name) {
        const parts: string[] = [];
        if (b.section_name) parts.push(b.section_name);
        if (b.row) parts.push(`Fila ${b.row}`);
        if (b.seat_number || b.seat) parts.push(`Butaca ${b.seat_number || b.seat}`);
        return parts.join(' · ');
      }
    }
    return '';
  }

  private showError(message: string): void {
    this.dialog.open(DialogGenericComponent, {
      data: {
        title: 'Error',
        subtitle: message,
        icon: 'error',
        buttons: [{ label: 'Cerrar', action: () => this.dialog.closeAll() }],
      },
    });
  }
}
